/**
 * ESP-IDF's wear-levelling layer (`components/wear_levelling`), read from a
 * partition's bytes. The layer keeps its configuration in the partition's
 * last sector and two copies of its state before it. The rest holds the
 * volume in pages, one of which is a spare "dummy" page that moves through
 * the partition, so a logical page lives at a physical page that depends on
 * where the dummy is and how many times it went round.
 */

/** Bytes of `wl_config_t` before its CRC, which the CRC covers. */
const CONFIG_CRC_LENGTH = 32;
/** `sizeof(wl_state_t)`: position records follow it. */
const STATE_LENGTH = 64;
/** Bytes of `wl_state_t` before its CRC. ESP-IDF upgrades a version 1 state when it mounts, so only version 2 is read. */
const STATE_CRC_LENGTH = 60;

export interface WearLevelling {
    pageSize: number;
    sectorSize: number;
    /** Bytes of the volume the layer presents. */
    volumeSize: number;
    /** The dummy page's position, in pages from the partition's start. */
    dummy: number;
    /** How many times the dummy page went round the partition. */
    moves: number;
}

/** What `wearLevelling` throws for a partition whose configuration is valid and whose state is not. */
export class BadWearLevelling extends Error {}

/**
 * The wear-levelling layer of a partition, or undefined when its last sector
 * holds no valid configuration for a partition of this size.
 */
export function wearLevelling(partition: Uint8Array): WearLevelling | undefined {
    const view = new DataView(partition.buffer, partition.byteOffset, partition.byteLength);
    const size = partition.length;
    // The configuration takes the last sector; the smallest flash sector is 4 KB.
    const cfgAt = size - 0x1000;
    if (cfgAt < 0) return undefined;
    const config = partition.subarray(cfgAt, cfgAt + CONFIG_CRC_LENGTH + 4);
    if (crc32(config.subarray(0, CONFIG_CRC_LENGTH)) !== view.getUint32(cfgAt + CONFIG_CRC_LENGTH, true))
        return undefined;
    const u32 = (at: number) => view.getUint32(at, true);
    const partitionSize = u32(cfgAt + 4);
    const pageSize = u32(cfgAt + 8);
    const sectorSize = u32(cfgAt + 12);
    const recordSize = u32(cfgAt + 20);
    if (partitionSize !== size || sectorSize !== 0x1000 || pageSize === 0 || pageSize % sectorSize !== 0) {
        return undefined;
    }

    // WL_Flash::config: a state takes whole sectors, enough for one record per sector.
    const stateSize = Math.max(sectorSize, roundUp(STATE_LENGTH + (size / sectorSize) * recordSize, sectorSize));
    const cfgSize = sectorSize;
    const states = [size - cfgSize - 2 * stateSize, size - cfgSize - stateSize];
    const volumeSize = (Math.floor((size - 2 * stateSize - cfgSize) / pageSize) - 1) * pageSize;
    if (volumeSize <= 0) throw new BadWearLevelling("the partition is too small for wear levelling");

    // WL_Flash::init takes the first copy whose CRC checks.
    const at = states.find((at) => crc32(partition.subarray(at, at + STATE_CRC_LENGTH)) === u32(at + STATE_CRC_LENGTH));
    if (at === undefined) throw new BadWearLevelling("neither copy of the wear-levelling state is valid");
    const maxPosition = u32(at + 4);
    const moves = u32(at + 8);
    const device = u32(at + 28);
    // WL_Flash::recoverPos: the dummy is at the first position record that is not written.
    let dummy = 0;
    while (dummy < maxPosition && recordWritten(partition, at + STATE_LENGTH + dummy * recordSize, device, dummy))
        dummy++;
    if (dummy === maxPosition && dummy !== 0) dummy--;
    return { pageSize, sectorSize, volumeSize, dummy, moves };
}

/** A record holds the CRCs of `wl_device_id + n * 4 + i` for i in 0..3 (WL_Flash::OkBuffSet). */
function recordWritten(partition: Uint8Array, at: number, device: number, n: number): boolean {
    const view = new DataView(partition.buffer, partition.byteOffset, partition.byteLength);
    const word = new DataView(new ArrayBuffer(4));
    for (let i = 0; i < 4; i++) {
        word.setUint32(0, (device + n * 4 + i) >>> 0, true);
        if (crc32(new Uint8Array(word.buffer)) !== view.getUint32(at + i * 4, true)) return false;
    }
    return true;
}

/** Where the volume's byte `logical` is in the partition (WL_Flash::calcAddr). */
export function physicalAddress(wl: WearLevelling, logical: number): number {
    const size = wl.volumeSize;
    let physical = (size - ((wl.moves * wl.pageSize) % size) + logical) % size;
    if (physical >= wl.dummy * wl.pageSize) physical += wl.pageSize;
    return physical;
}

/** The volume the layer presents, gathered page by page from the partition. */
export function volumeOf(wl: WearLevelling, partition: Uint8Array): Uint8Array {
    const volume = new Uint8Array(wl.volumeSize);
    for (let page = 0; page < wl.volumeSize; page += wl.pageSize) {
        const from = physicalAddress(wl, page);
        volume.set(partition.subarray(from, from + wl.pageSize), page);
    }
    return volume;
}

let table: Uint32Array | undefined;

/** ESP-IDF's `crc32_le(UINT32_MAX, ...)`, which the layer uses for every CRC. */
export function crc32(bytes: Uint8Array): number {
    if (table === undefined) {
        table = new Uint32Array(256);
        for (let n = 0; n < 256; n++) {
            let c = n;
            for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
            table[n] = c >>> 0;
        }
    }
    let crc = 0;
    for (const byte of bytes) crc = table[(crc ^ byte) & 0xff]! ^ (crc >>> 8);
    return ~crc >>> 0;
}

function roundUp(value: number, unit: number): number {
    return Math.ceil(value / unit) * unit;
}
